<template>
    <App title="Asset Bundle Receipt">
        <div class="mx-auto py-4 px-3 container">

            <div class="d-flex align-items-center justify-content-between mb-4 d-print-none">
                <button
                    class="btn btn-link btn-sm text-muted text-decoration-none d-flex align-items-center gap-1 p-0"
                    @click="backToPrevious"
                >
                    <i class="fas fa-arrow-left me-1"></i>
                    Back
                </button>
                <div class="ms-auto d-flex align-items-center gap-2">
                    <button
                        class="btn btn-dark btn-sm d-flex align-items-center gap-2"
                        style="font-size:12px;letter-spacing:.02em"
                        @click="window.print()"
                    >
                        <i class="fas fa-print"></i>
                        Print Receipt
                    </button>
                </div>
            </div>

            <div class="bundle-receipt bg-white border rounded-0 overflow-hidden p-2">
                <!-- top accent stripe -->
                <div class="bundle-stripe"></div>

                <!-- ── Header ── -->
                <div class="row g-0 px-4 pt-4 pb-3 align-items-start">
                    <!-- left: brand + title -->
                    <div class="col">
                        <div class="d-flex align-items-center gap-2 mb-3">
                            <div class="brand-icon">
                                <i class="fas fa-layer-group"></i>
                            </div>
                            <div>
                                <p class="brand-text">Financier Asset Tracking</p>
                                <p class="brand-subtext">Bundle Management System</p>
                            </div>
                        </div>
                        <h1 class="receipt-title">Asset Bundle Receipt</h1>
                        <p class="receipt-ref">{{ bundle.reference }}</p>
                    </div>

                    <!-- right: status + dates -->
                    <div class="col-auto text-end">
                        <span class="status-badge">
                            <span class="status-dot"></span> Assigned
                        </span>
                        <div class="receipt-dates">
                            <div>Created &nbsp;<strong>{{ formatDate(bundle.created_at) }}</strong></div>
                        </div>
                    </div>
                </div>

                <!-- ── Inventory Summary ── -->
                <div class="px-4 pb-4">
                    <div class="summary-card border rounded-2 p-4 bg-light">
                        <div class="d-flex align-items-center gap-4">
                            <div class="summary-count">
                                <span class="count-number">{{ bundle.quantity }}</span>
                                <span class="count-label text-uppercase">Total Engines</span>
                            </div>
                            <div class="vr" style="height: 60px; opacity: 0.1;"></div>
                            <div class="summary-info">
                                <p class="info-label text-uppercase">Source Location</p>
                                <p class="info-value">{{ bundle.source }} <small class="text-muted"></small></p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- ── Meta Grid ── -->
                <div class="row g-0 px-4 py-3 border-top border-bottom meta-grid">
                    <div class="col-6 col-md-4 py-2">
                        <p class="meta-label">Reference No.</p>
                        <p class="meta-value">{{ bundle.reference }}</p>
                    </div>
                    <div class="col-6 col-md-4 py-2">
                        <p class="meta-label">Financier</p>
                        <p class="meta-value">{{ bundle.financier }}</p>
                    </div>
                    <div class="col-6 col-md-4 py-2">
                        <p class="meta-label">Created By</p>
                        <p class="meta-value">{{ bundle.admin || 'System' }}</p>
                    </div>
                </div>

                <!-- ── Engines Table ── -->
                <div class="px-4 py-4">
                    <h3 class="section-title mb-3">Assigned Engines</h3>
                    <Datatable :options="tableOptions" />
                </div>

                <!-- ── Footer ── -->
                <div class="d-flex justify-content-between align-items-end px-4 py-4 border-top">
                    <div>
                        <div class="sig-line mb-2"></div>
                        <p class="sig-name">{{ bundle.admin || 'Authorized Signatory' }}</p>
                        <p class="sig-role text-muted">{{ bundle.admin.role || 'Authorized' }}</p>
                    </div>
                    <div class="text-end text-muted" style="font-size: 11px;">
                        <p class="mb-1">Generated on {{ today }}</p>
                        <p class="mb-0 fw-bold">Bundle Management System</p>
                    </div>
                </div>
            </div>

        </div>
    </App>
</template>

<script lang="ts">
import App from '@/vue/templates/App.vue'
import Datatable from '@/vue/components/Datatable.vue'
import { Component, Vue, toNative, Prop } from 'vue-facing-decorator'
import { CellRender, Column } from '@/vue/utils/Datatable'

@Component({
    components: { App, Datatable },
})
class BundleSingle extends Vue {
    @Prop({ type: Object, required: true })
    readonly bundle!: any

    get tableOptions() {
        return {
            ajax: {
                url: this.route('bundle.ajax.engines', { bundle: this.bundle.id }),
                method: 'GET',
            },
            columns: [
                {
                    data: 'id',
                    title: 'ID',
                    width: '50px',
                },
                {
                    data: 'serial',
                    title: 'Serial Number',
                },
              
                {
                    data: 'created_at',
                    title: 'Date Assigned',
                    render: CellRender.date(),
                }
            ],
           
        }
    }

    backToPrevious() {
        if (this.bundle.financier_slug) {
            this.$inertia.visit(this.route('financier.page.single', this.bundle.financier_slug))
        } else {
            window.history.back()
        }
    }

    formatDate(dateStr: string): string {
        if (!dateStr) return '—'
        return new Date(dateStr).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        })
    }

    get today(): string {
        return new Date().toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        })
    }

    get window() {
        return window
    }
}

export default toNative(BundleSingle)
</script>

<style scoped lang="scss">
/* Same styles as before */
.bundle-receipt {
    box-shadow: 0 10px 25px rgba(0,0,0,0.05);
}

.bundle-stripe {
    height: 6px;
    background: linear-gradient(90deg, #1e3a2f 0%, #3a7d52 100%);
}

.brand-icon {
    width: 36px;
    height: 36px;
    background: #1e3a2f;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    font-size: 1.1rem;
}

.brand-text {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    margin-bottom: 0;
    line-height: 1;
}

.brand-subtext {
    font-size: 10px;
    color: #6b7280;
    margin-bottom: 0;
    line-height: 1.4;
}

.receipt-title {
    font-size: 2.25rem;
    font-weight: 800;
    color: #111827;
    margin-top: 1rem;
    margin-bottom: 0.25rem;
}

.receipt-ref {
    font-family: 'Monaco', 'Consolas', monospace;
    font-size: 12px;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.1em;
}

.status-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: #ecfdf5;
    color: #065f46;
    padding: 6px 16px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.status-dot {
    width: 8px;
    height: 8px;
    background: #10b981;
    border-radius: 50%;
}

.receipt-dates {
    font-size: 11px;
    color: #6b7280;
    margin-top: 0.75rem;
    line-height: 1.6;
}

.summary-card {
    background: #f9fafb !important;
}

.summary-count {
    display: flex;
    flex-direction: column;
}

.count-number {
    font-size: 2.5rem;
    font-weight: 800;
    line-height: 1;
    color: #111827;
}

.count-label {
    font-size: 10px;
    font-weight: 600;
    color: #6b7280;
    letter-spacing: 0.1em;
    margin-top: 4px;
}

.info-label {
    font-size: 10px;
    font-weight: 600;
    color: #6b7280;
    letter-spacing: 0.1em;
    margin-bottom: 4px;
}

.info-value {
    font-size: 1.25rem;
    font-weight: 700;
    color: #111827;
    margin-bottom: 0;
}

.meta-grid {
    background: #fff;
}

.meta-label {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    color: #9ca3af;
    letter-spacing: 0.05em;
    margin-bottom: 2px;
}

.meta-value {
    font-size: 13px;
    font-weight: 600;
    color: #111827;
    margin-bottom: 0;
}

.section-title {
    font-size: 14px;
    font-weight: 700;
    text-transform: uppercase;
    color: #374151;
    letter-spacing: 0.05em;
}

.sig-line {
    width: 150px;
    border-top: 2px solid #111827;
}

.sig-name {
    font-size: 14px;
    font-weight: 700;
    color: #111827;
    margin-bottom: 0;
}

.sig-role {
    font-size: 12px;
}

@media print {
    .container {
        max-width: 100% !important;
        width: 100% !important;
        padding: 0 !important;
        margin: 0 !important;
    }
    .bundle-receipt {
        border: none !important;
        box-shadow: none !important;
    }
    .d-print-none {
        display: none !important;
    }
}
</style>